import type { Route } from "./+types/newsletter-archive";
import { Link } from "react-router";
import { content } from "../lib/content";
import { pageMeta } from "../lib/meta";
import { formatUtcShortDate } from "../utils/formatDate";
import { OG_IMAGE } from "../config/heroArt";
import InlineSubscribe from "../components/content/InlineSubscribe/InlineSubscribe";

// The site asks for an email address on nine surfaces and, until this page,
// offered no way to see what arrives in exchange for one. "Occasional notes on
// system design" is a claim. A back issue somebody can read before deciding is
// evidence, and it is the cheapest thing on the site to build once the campaign
// records exist.
//
// SSR and indexable, unlike /search: these are pages worth arriving on.
export async function loader() {
  // Never 500 an archive over an API blip. An empty archive reads as "nothing
  // published yet", which is also true on the day this ships and is a far better
  // failure than an error page on a route the newsletter CTA links to.
  try {
    const { items } = await content.newsletterIssues();
    return { issues: items };
  } catch {
    return { issues: [] };
  }
}

export function meta() {
  return pageMeta({
    title: "Newsletter archive | Gazar",
    description:
      "Every past issue of the gazar.dev newsletter, in full. Read what arrives before deciding whether to subscribe.",
    path: "/newsletter/archive",
    image: OG_IMAGE.learnings,
  });
}

export default function NewsletterArchive({ loaderData }: Route.ComponentProps) {
  const { issues } = loaderData;
  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="font-sans text-[40px] sm:text-[52px] font-bold text-brand-navy leading-[1.1] tracking-[-0.022em] mb-2">
        Newsletter archive
      </h1>
      <p className="text-[21px] text-brand-navy/70 mb-10">
        Every issue that has gone out, in full. No schedule, no summaries of other people&rsquo;s
        posts, and nothing here that was not sent to the list first.
      </p>

      {issues.length === 0 ? (
        // Said plainly rather than dressed up. A "coming soon" panel on a page
        // reached from a subscribe CTA is worse than an honest empty one.
        <p className="text-brand-navy/70 mb-10">
          Nothing is published here yet. The next issue will be.
        </p>
      ) : (
        <ul className="space-y-6 mb-16">
          {issues.map((issue) => (
            <li key={issue.publicSlug} className="border-b border-brand-navy/10 pb-6 last:border-0">
              <p className="text-sm text-brand-navy/50 mb-1">{formatUtcShortDate(issue.sentAt)}</p>
              <h2 className="text-2xl font-bold text-brand-navy">
                <Link
                  to={`/newsletter/${issue.publicSlug}`}
                  className="underline decoration-brand-navy/30 underline-offset-4 hover:decoration-brand-navy/80"
                >
                  {issue.subject}
                </Link>
              </h2>
            </li>
          ))}
        </ul>
      )}

      {/* The ask, at the point where somebody has just read what they would be
          agreeing to. Tagged so this page reads against its own baseline rather
          than disappearing into the /newsletter number. */}
      <InlineSubscribe source="newsletter-archive" />
    </div>
  );
}
